import React, { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { createUseStyles } from '../../../../theming';
import { CollaborativeSource, CreateReminderOptions } from '../../../../interfaces/api/Collaborative';
import { Controller, useForm } from 'react-hook-form';
import { CriticitySelect } from '../../../ApiField';
import DateTimePicker from '../../../DateTimePicker';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';

const useStyles = createUseStyles({
  title: {
    width: 400,
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  formControl: {
    marginBottom: '16px !important',
  },
});

const defaultFormValues = {
  title: '',
  text: '',
  criticity: undefined,
  dateRecall: moment().format('YYYY-MM-DD HH:mm:ss'),
  timeBeforeAlert: 0,
};

interface Props {
  reminderSource?: CollaborativeSource;
  open: boolean;
  onClose: () => void;
  onValidate: (createReminderOptions: CreateReminderOptions) => void;
}

const AddReminderDialog: FC<Props> = ({ open, reminderSource, onClose, onValidate }) => {
  const classes = useStyles();

  const [formError, setFormError] = useState<string | undefined>();
  const {
    reset,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<CreateReminderOptions>({
    defaultValues: {
      ...defaultFormValues,
      attachedTo: reminderSource,
    },
  });

  useEffect(() => {
    setFormError(undefined);
  }, []);

  const onSubmit = async (createReminderOptions: CreateReminderOptions) => {
    try {
      setFormError(undefined);
      await onValidate(createReminderOptions);
      reset(defaultFormValues);
      onClose();
    } catch (e) {
      setFormError(e.message);
    }
  };

  return (
    <Dialog onClose={onClose} scroll='paper' open={open}>
      <DialogTitle className={classes.title}>Créer un rappel</DialogTitle>

      <DialogContent>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <Controller
            name='title'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Titre'
                placeholder='Retrouver le rappel plus tard'
                variant='outlined'
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='text'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Texte'
                placeholder='Contenu du rappel (facultatif)'
                variant='outlined'
                multiline
                rows={3}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='criticity'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <CriticitySelect
                {...field}
                className={classes.formControl}
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <Controller
            name='dateRecall'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field: { onChange, ...fieldOthers }, fieldState: { error } }) => (
              <DateTimePicker
                {...fieldOthers}
                onChange={(date) => onChange(date ? date.format('YYYY-MM-DD HH:mm:ss') : undefined)}
                className={classes.formControl}
                label='Date de rappel'
                format='DD/MM/YYYY HH:mm'
                inputVariant='outlined'
                helperText={error?.message}
                error={undefined !== error}
                disablePast
                fullWidth
                ampm={false}
              />
            )}
          />

          <Controller
            name='timeBeforeAlert'
            control={control}
            rules={{
              required: { value: true, message: 'Le champ est requis' },
              min: { value: 1, message: 'La durée doit être superieur à 0' },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.formControl}
                label='Rappel avant (min)'
                type='number'
                InputProps={{ inputProps: { min: 0 } }}
                variant='outlined'
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />
        </MuiPickersUtilsProvider>
      </DialogContent>

      <DialogActions>
        <Button type='button' onClick={onClose} color='primary'>
          Annuler
        </Button>

        <Button type='submit' color='primary' disabled={isSubmitting} onClick={handleSubmit(onSubmit)}>
          Créer
        </Button>
      </DialogActions>

      {formError && <p className={classes.error}>{formError}</p>}
    </Dialog>
  );
};

export default AddReminderDialog;
